import { SEMANA_OBJETIVO_MAX, type SemanaPublic } from '@app/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { semanaApi } from '../api/semana.api';

import { InfoSmart } from './InfoSmart';

interface Props {
  semana: SemanaPublic;
}

// D-044 (19/10/2026): el objetivo de la semana es el texto principal del
// encabezado, siempre visible. Mientras no hay objetivo, el encabezado muestra
// el campo para escribirlo. Se puede cambiar planificando y con la semana en
// curso (Javier: Ailen todavía está aprendiendo a planificar). No es un título
// de página (CLAUDE.md §5.8): es un dato que cambia cada semana.
export function ObjetivoSemana({ semana }: Props): JSX.Element {
  const es = useI18n();
  const t = es.tareas.objetivo;
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);

  const definir = useMutation({
    mutationFn: (objetivo: string) => semanaApi.definirObjetivo(objetivo),
    onSuccess: (s) => {
      queryClient.setQueryData(qk.tareas.semanaActual, s);
      toast.success(t.exito);
      setEditando(false);
    },
    onError: (e) => setError(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  function abrirEdicion(): void {
    setTexto(semana.objetivo ?? '');
    setError(null);
    setEditando(true);
  }

  function enviar(): void {
    const objetivo = texto.trim();
    if (objetivo.length === 0) {
      setError(t.errorVacio);
      return;
    }
    definir.mutate(objetivo);
  }

  if (semana.objetivo && !editando) {
    return (
      <div className="flex items-center gap-2">
        <p data-testid="objetivo-semana" className="text-lg font-semibold leading-snug text-text-marca">
          {semana.objetivo}
        </p>
        <button
          type="button"
          onClick={abrirEdicion}
          aria-label={t.editar}
          className="shrink-0 text-text-faint hover:text-text-strong"
        >
          <Pencil size={16} />
        </button>
        <InfoSmart />
      </div>
    );
  }

  // Todo en una fila de 40 px de alto (campo, info y botones), para que quede
  // alineado con "Guardar planificación" a la derecha del encabezado.
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        enviar();
      }}
      className="flex flex-col gap-1"
      noValidate
    >
      <label htmlFor="objetivo-semana" className="sr-only">
        {t.etiqueta}
      </label>
      <div className="flex items-center gap-2">
        <Input
          id="objetivo-semana"
          value={texto}
          maxLength={SEMANA_OBJETIVO_MAX}
          placeholder={t.placeholder}
          onChange={(e) => setTexto(e.target.value)}
          className="flex-1"
        />
        <InfoSmart />
        <Button type="submit" variant="outline" disabled={definir.isPending}>
          {definir.isPending ? t.guardando : t.guardar}
        </Button>
        {semana.objetivo && (
          <Button type="button" variant="ghost" onClick={() => setEditando(false)}>
            {t.cancelar}
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
    </form>
  );
}
